/// <reference types="@types/google.maps" />

import { Address } from '@/types/common';
import { InputRules } from '@/types/inputs';
import { buildInputRulesObject } from '@/utils/buildInputRulesObject';
import { Box, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { usePlacesWidget } from 'react-google-autocomplete';

const googlePlaceKey = import.meta.env.VITE_GOOGLE_PLACE_KEY;

type SVPlacesAutocompleteProps = {
  name: string;
  label: string;
  handleSelectPlace: (address: Address) => void;
  rules?: InputRules;
  allowInternational?: boolean;
};

export default function SVPlacesAutocomplete({
  name,
  label,
  handleSelectPlace,
  rules,
  allowInternational = false,
}: SVPlacesAutocompleteProps) {
  const methods = useFormContext();
  const builtRulesObject = buildInputRulesObject(label, rules);

  function findAddressDetail(
    details: google.maps.places.PlaceResult | null,
    type: string,
  ) {
    return details?.address_components?.find(addressComponent =>
      addressComponent.types.includes(type),
    )?.short_name;
  }

  const { ref: addressAutocompleteRef } = usePlacesWidget({
    apiKey: googlePlaceKey,
    options: {
      componentRestrictions: allowInternational ? undefined : { country: 'us' },
      types: ['address'],
    },
    onPlaceSelected: data => {
      handleSelectPlace({
        line1: `${findAddressDetail(data, 'street_number')} ${findAddressDetail(data, 'route')}`,
        line2: '',
        city: findAddressDetail(data, 'locality') || '',
        state: findAddressDetail(data, 'administrative_area_level_1') || '',
        postalCode: findAddressDetail(data, 'postal_code') || '',
      });
    },
  });

  return (
    <Controller
      control={methods.control}
      name={name}
      rules={builtRulesObject}
      render={({
        field: { value, onBlur, onChange },
        fieldState: { error },
      }) => {
        return (
          <Box sx={{ marginBottom: 2 }}>
            <TextField
              value={value}
              fullWidth
              label={label}
              inputRef={addressAutocompleteRef}
              error={!!error?.message}
              helperText={error?.message}
              onBlur={onBlur}
              onChange={onChange}
            />
          </Box>
        );
      }}
    />
  );
}
